import { Component, OnInit, input, inject } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, ActivatedRoute, ParamMap } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../../amplify/data/resource';

const client = generateClient<Schema>();

import { MatListModule } from '@angular/material/list';

import { AmplifyModelService } from '../../services/amplify-model-service.service';
import outputs from '../../../../amplify_outputs.json';

@Component({
    selector: 'app-list-view',
    standalone: true,
    imports: [MatListModule, MatIconModule, MatButtonModule, MatToolbarModule],
    templateUrl: './list-view.component.html',
    styleUrl: './list-view.component.css'
})
export class ListViewComponent implements OnInit {
  private ams = inject(AmplifyModelService);
  itemsArr: any[] = [];
  modelName = input('');
  mdl: string | null = '';
  displayField: string = '';
  
  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      console.log('ListViewComponent params', params);
      this.mdl = params.get('model');
      this.setDisplayField();
      this.listItems();
    });
  }

  constructor(private route: ActivatedRoute, private router: Router) { }

  /**
   * Determine which field to use for displaying list items
   * Priority: name -> title -> first non-system field -> id
   */
  setDisplayField() {
    if (!this.mdl) return;
    
    try {
      const modelFields = outputs.data.model_introspection.models[this.mdl as keyof typeof outputs.data.model_introspection.models]?.fields;
      if (!modelFields) {
        this.displayField = 'id';
        return;
      }

      // Check for name field first
      if ((modelFields as any).name) {
        this.displayField = 'name';
        return;
      }

      // Check for title field second
      if ((modelFields as any).title) {
        this.displayField = 'title';
        return;
      }

      // Find first non-system field
      const systemFields = ['id', 'createdAt', 'updatedAt'];
      const fieldNames = Object.keys(modelFields);
      const firstNonSystemField = fieldNames.find(fieldName => 
        !systemFields.includes(fieldName) && 
        !fieldName.endsWith('Id') && // Skip foreign key fields
        (modelFields as any)[fieldName].type === 'String' // Prefer string fields for display
      );

      if (firstNonSystemField) {
        this.displayField = firstNonSystemField;
        return;
      }

      // If no string fields, try any non-system field
      const anyNonSystemField = fieldNames.find(fieldName => 
        !systemFields.includes(fieldName) && 
        !fieldName.endsWith('Id')
      );

      this.displayField = anyNonSystemField || 'id';
      
      console.log(`ListViewComponent: Using display field '${this.displayField}' for model '${this.mdl}'`);
    } catch (error) {
      console.warn('Error determining display field, falling back to id:', error);
      this.displayField = 'id';
    }
  }

  /**
   * Get the display value for a list item.
   *
   * Checks the model-level `display_value` config first (AmplifyModelService
   * .getModelConfig().Models[model].display_value) - this is how models with
   * no name/title/plain-string field, like ServiceAddress and its join
   * models, get a meaningful label instead of falling through to id. Only
   * falls back to the name/title/first-string-field heuristic in
   * setDisplayField() when no display_value config exists for this model.
   */
  getDisplayValue(item: any): string {
    if (!item) return 'Unknown';

    const configured = this.ams.getDisplayValueForModel(this.mdl, item);
    if (configured) return configured;

    const value = item[this.displayField];
    
    // Handle different field types
    if (value === null || value === undefined) {
      return item.id || 'Unknown';
    }
    
    if (typeof value === 'string') {
      return value;
    }
    
    if (typeof value === 'object') {
      // For complex objects, try to find a meaningful string representation
      return JSON.stringify(value);
    }
    
    return String(value);
  }

  /**
   * Get a descriptive subtitle for complex models like FieldConfig
   */
  getSubtitle(item: any): string {
    if (!item || !this.mdl) return '';
    
    // Special handling for FieldConfig
    if (this.mdl === 'FieldConfig') {
      return `${item.model_name || 'Unknown'}.${item.field_name || 'Unknown'}`;
    }
    
    // For other models, could add more specific logic here
    return '';
  }

  listItems() {
    console.log('this.listItems starting for mdl', this.mdl);
    if (this.mdl) {
      this.ams.listItemsForModel(this.mdl).subscribe({
        next: ({ items, isSynced }) => {
          this.itemsArr = items;
        },
      });
    }
  }

  itemClick(item: any) {
    console.log('itemClick', item, 'this.mdl', this.mdl);
    if (!this.mdl) return;
    let overrideUrl = this.ams.listItemLinkOverrideForModel(this.mdl);
    if (typeof overrideUrl == 'string') {
      this.router.navigate([overrideUrl + '/' + item.id]);
    }
    else {
      this.router.navigate(['/dashboard/form/' + this.mdl + '/' + item.id]);
    }
  }

  newClick() {
    this.router.navigate(['/dashboard/form/' + this.mdl + '/-1']);
  }

}
